/**
 * The motor pool's gate lanes (GATE_LANE; 71.7's gate walks found a tree 16 people out of s71-9's front gate, towed guns
 * ~35 out on s71-5 and s71-8, trees at 32 on s71-12 and a rock at 44 on s71-1 — on flat ground, in the way of what the pool
 * unpacks). Each gate that opens outside keeps a rectangle out from it, as wide as its ramp (core/terraces.ts gateRamps: the
 * gate's span and TERRACE.rampMarginPeople) and GATE_LANE.sidePeople more a side, GATE_LANE.outPeople long:
 * - the base planner's yards pass claims it, so a structure standing in it moves (core/basePlanner.ts planYards);
 * - at setup, on the action clock, every rock or flora whose walk footprint reaches into it is taken off the map, as the
 *   capture circles are cleared (core/pointClearing.ts) — after the dressing, before the forest.
 * Pure.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { GATE_LANE } from '../constants/action';
import { MODULE_CELL, MODULE_SPAN } from '../constants/modules';
import { vecCross, vecDot, vecNormalize, vecScale, vecSub } from '../math';
import { TERRACE } from './terraceGround';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { footprintObstacles, personMover, structureFrame } from './hits';
import { layoutFor } from './modules';
import { OUTSIDE, roomGraph } from './roomGraph';
import { initFloraDestructionState } from './floraTracking';

/** A gate's lane on the ground: its middle (a unit direction), its axes (tz out from the gate) and half extents (world). */
export interface GateLane { planetIndex: number; c: Vec3; tx: Vec3; tz: Vec3; hx: number; hz: number }

/** The lanes out of a motor pool's gates (none for a structure without a layout's outside gates). */
export function poolGateLanes(state: Pick<GameState, 'planets' | 'config'>, pool: StructureState): GateLane[] {
  const planet = state.planets[pool.planetIndex], layout = planet ? layoutFor(pool, state.config) : null;
  if (!planet || !layout) return [];
  const person = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k, R = planet.radius;
  const up = vecNormalize(pool.surfaceDir), { tx, tz } = structureFrame(pool, up);
  const at = (x: number, z: number) => vecNormalize({ x: up.x + (tx.x * x + tz.x * z) * person / R, y: up.y + (tx.y * x + tz.y * z) * person / R, z: up.z + (tx.z * x + tz.z * z) * person / R });
  const out: GateLane[] = [];
  for (const w of roomGraph(layout, new Set(), undefined).ways) {
    if ((w.kind !== 'gate' && w.kind !== 'gateWide') || (w.a !== OUTSIDE && w.b !== OUTSIDE)) continue;
    const sgn = w.b === OUTSIDE ? 1 : -1, nx = sgn * w.nx, nz = sgn * w.nz, half = GATE_LANE.outPeople / 2;
    const c = at(w.x + nx * half, w.z + nz * half);
    const n = { x: tx.x * nx + tz.x * nz, y: tx.y * nx + tz.y * nz, z: tx.z * nx + tz.z * nz };
    const lz = vecNormalize(vecSub(n, vecScale(c, vecDot(n, c)))), lx = vecNormalize(vecCross(c, lz));
    const side = (MODULE_SPAN[w.kind] ?? 1) * MODULE_CELL / 2 + TERRACE.rampMarginPeople + GATE_LANE.sidePeople;
    out.push({ planetIndex: pool.planetIndex, c, tx: lx, tz: lz, hx: side * person, hz: half * person });
  }
  return out;
}

/** Every standing motor pool's gate lanes. */
export function gateLanes(state: Pick<GameState, 'planets' | 'config' | 'structures'>): GateLane[] {
  return state.structures.filter(s => s.type === 'motorPool' && s.health > 0).flatMap(s => poolGateLanes(state, s));
}

/** Whether a disc (`dir`, `radius` world) on lane `l`'s planet reaches into it. */
export function inGateLane(l: GateLane, dir: Vec3, radius: number, R: number): boolean {
  const d = vecNormalize(dir);
  if (vecDot(d, l.c) <= 0) return false;
  const off = vecScale(vecSub(d, l.c), R);
  return Math.abs(vecDot(off, l.tx)) < l.hx + radius && Math.abs(vecDot(off, l.tz)) < l.hz + radius;
}

/**
 * The setup's pass (match/setup.ts, after the dressing): the action clock's gate lanes cleared of the map's rocks and flora
 * whose walk footprint (a person's, footprintObstacles) reaches in — a body's radius more, as the capture circles. Structures
 * are the planner's (planYards). The same state when nothing stands in a lane (no pools: untouched).
 */
export function clearGateLanes(state: GameState): GameState {
  if (state.config.clock !== 'action') return state;
  const lanes = gateLanes(state);
  if (!lanes.length) return state;
  const ps = personSetOf(state);
  const rocks = new Set<number>(), flora = new Set<number>();
  for (const pi of [...new Set(lanes.map(l => l.planetIndex))].sort((a, b) => a - b)) {
    const planet = state.planets[pi];
    if (!planet) continue;
    const here = lanes.filter(l => l.planetIndex === pi);
    for (const o of footprintObstacles(state, pi, personMover(ps)).list) {
      if (o.ref?.kind !== 'rock' && o.ref?.kind !== 'flora') continue;
      if (here.some(l => inGateLane(l, o.dir, o.radius + ps.unitRadius, planet.radius))) (o.ref.kind === 'rock' ? rocks : flora).add(o.ref.id);
    }
  }
  if (rocks.size === 0 && flora.size === 0) return state;
  let next: GameState = { ...state };
  if (rocks.size) next.rocks = state.rocks.filter(r => !rocks.has(r.id));
  if (flora.size) {
    next.flora = state.flora.filter(f => !flora.has(f.id));
    if (next.floraDestruction) next = { ...next, floraDestruction: initFloraDestructionState(next.flora) };
  }
  return next;
}
